'use client';

import { useTranslations } from '@heidi/runtime/i18n';
import { cn } from '@heidi/utils';
import { formatCjkAwareDisplayName } from '@heidi/utils/i18n';
import { Loader2 } from 'lucide-react';
import { AnimatePresence } from 'motion/react';
import * as m from 'motion/react-m';
import { useEffect, useMemo, useRef, useState } from 'react';

import {
  hasSalutation,
  stripDocumentSignOff,
} from '../../../../utils/stripDocumentSignOff';
import type { EnhancedLine } from '../mic-test-preview/types';

const LAYOUT_EASE = [0.4, 0, 0.2, 1] as const;
const LINE_REVEAL_INTERVAL_MS = 800;

function StatusPill({ label }: { label: string }) {
  return (
    <m.div
      layoutId="referral-status-pill"
      className="my-1 flex w-full items-center gap-2.5 rounded-xl bg-surface-accent px-4 py-2.5"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{
        layout: { duration: 0.7, ease: LAYOUT_EASE },
        opacity: { duration: 0.2 },
      }}
    >
      <Loader2 className="size-4 animate-spin text-accent-forest" />
      <span className="text-sm font-semibold text-accent-forest">{label}</span>
    </m.div>
  );
}

interface ReferralContentProps {
  visible: boolean;
  /**
   * Pre-parsed sections from the per-specialty document body served by
   * `/onboarding-v2-assets`. While the assets are still resolving the
   * renderer shows the loading pill alone; once the sections land they
   * reveal one line at a time.
   */
  sections?: EnhancedLine[][] | null;
  /**
   * Per-specialty document name (e.g. "Referral Letter", "Treatment Plan",
   * "Progress Summary"). Drives the loading-pill copy so it matches the
   * document the user is generating instead of always saying "referral
   * letter". Falls back to the localized generic document label during the
   * brief assets-loading window.
   */
  documentType?: string;
  /**
   * User name appended after the BE-served "Kind regards," salutation, in
   * place of the impersonal specialty signature ("Acupuncturist",
   * "Addiction Medicine Specialist", …) that the seeded document body
   * ships with. See `stripDocumentSignOff` for the strip side.
   */
  firstName?: string;
  lastName?: string;
  /**
   * Localized specialty label appended in italics beneath the user's name,
   * mirroring the clinical-note signature (see `NoteSignature`). Only added
   * when the document has a salutation we preserved.
   */
  specialtyName?: string;
  displayLanguage?: string | null;
  onComplete?: () => void;
}

/** A reveal line; `italic` flags the appended specialty signature line. */
type SignatureLine = EnhancedLine & { italic?: boolean };

export function ReferralContentRenderer({
  visible,
  sections,
  documentType,
  firstName,
  lastName,
  specialtyName,
  displayLanguage,
  onComplete,
}: ReferralContentProps) {
  const { t } = useTranslations();

  // Strip the BE-provided specialty signature ("…\nAcupuncturist") that
  // every per-specialty document body ends with. The "Kind regards,"
  // salutation is intentionally kept so the rendered document reads
  // "Kind regards, / <user's name>" instead of "Kind regards, /
  // <Specialty>". Pass-through if no salutation is detected.
  const cleanedSections = useMemo(
    () => (sections ? stripDocumentSignOff(sections) : []),
    [sections]
  );
  const documentHasSalutation = useMemo(
    () => hasSalutation(cleanedSections),
    [cleanedSections]
  );

  // Append the user's full name as one more header-styled line so it
  // slots into the existing staggered reveal as "the next line after
  // Kind regards,". Only when (a) the document had a salutation we
  // preserved and (b) we have a name to insert — otherwise we'd append
  // a stray name to a non-letter document or end the letter with a
  // bare salutation.
  const lines: SignatureLine[] = useMemo(() => {
    const body: SignatureLine[] = cleanedSections.flat();
    const fullName = formatCjkAwareDisplayName(
      { firstName: firstName ?? '', lastName: lastName ?? '' },
      displayLanguage
    );
    if (documentHasSalutation && fullName) {
      body.push({ type: 'header', text: fullName });
      // Append the specialty in italics beneath the name, mirroring the
      // clinical-note signature (NoteSignature) so the document is signed
      // off by the clinician's name + specialty rather than name alone.
      if (specialtyName) {
        body.push({ type: 'paragraph', text: specialtyName, italic: true });
      }
    }
    return body;
  }, [
    cleanedSections,
    displayLanguage,
    documentHasSalutation,
    firstName,
    lastName,
    specialtyName,
  ]);
  const lineCount = lines.length;

  const [revealed, setRevealed] = useState(0);
  const [generating, setGenerating] = useState(true);
  const [done, setDone] = useState(false);
  const onCompleteRef = useRef(onComplete);
  onCompleteRef.current = onComplete;

  useEffect(() => {
    // Mount-based (NOT gated on `visible`): each document tab has its own
    // persistent instance, so the generation runs once and completes in the
    // background even if the user switches tabs — re-entering shows the same
    // consistent state with no restart/duplication. Bail while assets are
    // still loading (lineCount 0) so we don't fire onComplete on empty.
    if (done || lineCount === 0) return;
    let count = 0;
    const id = setInterval(() => {
      count += 1;
      setRevealed(count);
      if (count >= lineCount) {
        clearInterval(id);
        setTimeout(() => {
          setGenerating(false);
          setDone(true);
          onCompleteRef.current?.();
        }, 400);
      }
    }, LINE_REVEAL_INTERVAL_MS);
    return () => clearInterval(id);
  }, [done, lineCount]);

  const renderLine = (line: SignatureLine) => {
    if (line.italic) {
      return <p className="text-sm italic text-text-secondary">{line.text}</p>;
    }
    if (line.type === 'header') {
      return <p className="text-sm font-semibold text-text-primary">{line.text}</p>;
    }
    if (line.type === 'bullet') {
      return <p className="text-sm text-text-secondary">&bull; {line.text}</p>;
    }
    return <p className="text-sm text-text-secondary">{line.text}</p>;
  };

  return (
    <m.div
      className={cn('flex flex-col gap-3', !visible && 'hidden')}
      layout
      transition={{ layout: { duration: 0.7, ease: LAYOUT_EASE } }}
    >
      {lines.slice(0, revealed).map((line, i) => (
        <m.div
          key={i}
          layout
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{
            layout: { duration: 0.7, ease: LAYOUT_EASE },
            opacity: { duration: 0.6 },
          }}
        >
          {renderLine(line)}
        </m.div>
      ))}
      <AnimatePresence>
        {generating && (
          <StatusPill
            label={t('signup.previewReferralCreating', {
              document: documentType ?? t('common.document'),
            })}
          />
        )}
      </AnimatePresence>
    </m.div>
  );
}
